import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { grey, orange, green, blue } from '@mui/material/colors';

// Inquiry training: every opportunity the AI wizard filled in, with the email it read, what it filled
// in and what the user saved. The differences are what users corrected (or later changed); the
// parsing rules are derived from them (docs/inquiry-agent/).

const PAGE_SIZE = 50;

function show(v) {
    if (v === null || v === undefined || v === '') return <span style={{ color: grey[400] }}>—</span>;
    if (Array.isArray(v)) return v.map(x => (x === null || x === undefined || x === '' ? '—' : String(x))).join(' × ');
    if (typeof v === 'object') return Object.entries(v).map(([k, x]) => `${k}: ${x}`).join(', ');
    return String(v);
}

// The model that read the inquiry (the server labels answers from before it was recorded gpt-4o).
const modelLabel = r => r?.model || 'gpt-4o';

function ModelCell({ r }) {
    return <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{modelLabel(r)}</TableCell>;
}

function Section({ title, children }) {
    return (
        <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: grey[700], textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>{title}</Typography>
            {children}
        </Box>
    );
}

function Shipment({ s, ai }) {
    const routes = s.routes || [];
    return (
        <Table size='small'>
            <TableBody>
                <TableRow><TableCell sx={{ width: 110, color: grey[600] }}>Mode</TableCell><TableCell>{show(s.mode)}</TableCell></TableRow>
                {!ai && <TableRow><TableCell sx={{ color: grey[600] }}>Incoterm</TableCell><TableCell>{show(s.incoterm)}</TableCell></TableRow>}
                {routes.length === 0 && <TableRow><TableCell sx={{ color: grey[600] }}>Route</TableCell><TableCell>{show(null)}</TableCell></TableRow>}
                {routes.map((r, i) => (
                    <TableRow key={i}><TableCell sx={{ color: grey[600] }}>Route {i + 1}</TableCell><TableCell>{show(r.source)} → {show(r.destination)}</TableCell></TableRow>
                ))}
                {(s.products || []).map((p, i) => (
                    <TableRow key={`p${i}`}><TableCell sx={{ color: grey[600] }}>Cargo {i + 1}</TableCell><TableCell>{show(p)}</TableCell></TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function RecordDialog({ id, accesstoken, onClose }) {
    const [record, setRecord] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (!id) return;
        setRecord(null); setError(null);
        axios.get(`/api/admin/inquiry-training/${id}?accesstoken=${accesstoken}`)
            .then(({ data }) => setRecord(data))
            .catch(e => setError(e?.response?.data?.detail || e.message));
    }, [id, accesstoken]);
    return (
        <Dialog open={!!id} onClose={onClose} fullWidth maxWidth='xl'>
            <DialogTitle>
                {record ? `${record.organization} · ${dayjs(record.created).format('D MMM YYYY')} · ${record.from === 'mailbox' ? 'from the mailbox' : 'pasted'}` : 'Inquiry'}
                {record && <Typography sx={{ fontSize: 12, color: grey[600] }}>Opportunity {record.id} · status {record.status || 'draft'} · model {modelLabel(record)}{record.sender ? ` · sender ${record.sender}` : ''}</Typography>}
            </DialogTitle>
            <DialogContent>
                {error && <Alert severity='error'>{error}</Alert>}
                {!record && !error && <CircularProgress size={24} />}
                {record && (
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: '1 1 420px', minWidth: 0 }}>
                            <Section title='Email the AI read'>
                                <Paper variant='outlined' sx={{ p: 1.5, whiteSpace: 'pre-wrap', fontSize: 12, fontFamily: 'monospace', maxHeight: '70vh', overflow: 'auto' }}>{record.text}</Paper>
                            </Section>
                        </Box>
                        <Box sx={{ flex: '1 1 420px', minWidth: 0 }}>
                            {record.maybeReused && <Alert severity='warning' sx={{ mb: 2 }}>Both ends of the route changed. The opportunity may have been reused for another shipment, so these differences may not be corrections.</Alert>}
                            {record.reasons?.length > 0 && (
                                <Section title='Why, in the user’s words'>
                                    {record.reasons.map((x, i) => <Typography key={i} sx={{ fontSize: 13, mb: 1 }}>“{x.text}” <span style={{ color: grey[500] }}>{x.by}, {dayjs(x.created).format('D MMM HH:mm')}</span></Typography>)}
                                </Section>
                            )}
                            <Section title={`Differences (${record.differences.length})`}>
                                {record.differences.length === 0 ? <Typography sx={{ fontSize: 13, color: green[700] }}>The user saved what the AI filled in.</Typography> : (
                                    <Table size='small'>
                                        <TableHead><TableRow><TableCell>What</TableCell><TableCell>AI</TableCell><TableCell>Saved</TableCell></TableRow></TableHead>
                                        <TableBody>
                                            {record.differences.map((d, i) => (
                                                <TableRow key={i}>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{d.kind}: {d.what}{d.route ? ` (route ${d.route})` : ''}</TableCell>
                                                    <TableCell sx={{ color: orange[900] }}>{show(d.ai)}</TableCell>
                                                    <TableCell sx={{ color: green[800] }}>{show(d.saved)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </Section>
                            <Section title='What the AI filled in'><Shipment s={record.ai} ai /></Section>
                            <Section title='What the user saved'><Shipment s={record.saved} /></Section>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}

// Corrections users saved since the wizard started keeping its answer (2026-10-08), with their reasons.
function Corrections({ accesstoken }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(null);
    useEffect(() => {
        axios.get(`/api/admin/inquiry-training/corrections?accesstoken=${accesstoken}`)
            .then(({ data }) => setData(data))
            .catch(e => setError(e?.response?.data?.detail || e.message));
    }, [accesstoken]);
    if (error) return <Alert severity='error'>{error}</Alert>;
    if (!data) return <CircularProgress size={24} />;
    if (!data.records.length) return <Typography sx={{ fontSize: 13, color: grey[600] }}>No corrections yet. They appear here when a user changes what the AI filled in and saves the quote.</Typography>;
    return (
        <>
            <Paper variant='outlined'>
                <Table size='small'>
                    <TableHead><TableRow><TableCell>Saved</TableCell><TableCell>Workspace</TableCell><TableCell>User</TableCell><TableCell>Model</TableCell><TableCell>What changed</TableCell><TableCell>Why</TableCell></TableRow></TableHead>
                    <TableBody>
                        {data.records.map(r => (
                            <TableRow key={`${r.organizationId}-${r.id}`} hover sx={{ cursor: 'pointer' }} onClick={() => setOpen(r)}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(r.updated).format('D MMM YYYY HH:mm')}</TableCell>
                                <TableCell>{r.organization}</TableCell>
                                <TableCell>{r.updatedBy}</TableCell>
                                <ModelCell r={r} />
                                <TableCell><Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>{[...new Set(r.differences.map(d => d.what))].map(w => <Chip key={w} size='small' label={w} sx={{ bgcolor: blue[50], color: blue[900] }} />)}</Box></TableCell>
                                <TableCell sx={{ fontSize: 12 }}>{r.reasons.length ? r.reasons[r.reasons.length - 1].text : show(null)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
            <Dialog open={!!open} onClose={() => setOpen(null)} fullWidth maxWidth='xl'>
                {open && (
                    <>
                        <DialogTitle>
                            {open.organization} · {dayjs(open.updated).format('D MMM YYYY')}
                            <Typography sx={{ fontSize: 12, color: grey[600] }}>Opportunity {open.id} · {open.from === 'mailbox' ? 'from the mailbox' : 'pasted'} · model {modelLabel(open)} · saved by {open.updatedBy}</Typography>
                        </DialogTitle>
                        <DialogContent>
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                <Box sx={{ flex: '1 1 420px', minWidth: 0 }}>
                                    <Section title='Email the AI read'>
                                        <Paper variant='outlined' sx={{ p: 1.5, whiteSpace: 'pre-wrap', fontSize: 12, fontFamily: 'monospace', maxHeight: '70vh', overflow: 'auto' }}>{open.text}</Paper>
                                    </Section>
                                </Box>
                                <Box sx={{ flex: '1 1 420px', minWidth: 0 }}>
                                    <Section title='Why, in the user’s words'>
                                        {open.reasons.length ? open.reasons.map((x, i) => <Typography key={i} sx={{ fontSize: 13, mb: 1 }}>“{x.text}” <span style={{ color: grey[500] }}>{x.by}, {dayjs(x.created).format('D MMM HH:mm')}</span></Typography>) : <Typography sx={{ fontSize: 13, color: grey[600] }}>No reason given.</Typography>}
                                    </Section>
                                    <Section title='What changed'>
                                        <Table size='small'>
                                            <TableHead><TableRow><TableCell>What</TableCell><TableCell>AI</TableCell><TableCell>Saved</TableCell></TableRow></TableHead>
                                            <TableBody>
                                                {open.differences.map((d, i) => (
                                                    <TableRow key={i}><TableCell>{d.what}</TableCell><TableCell sx={{ color: orange[900] }}>{show(d.ai)}</TableCell><TableCell sx={{ color: green[800] }}>{show(d.saved)}</TableCell></TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Section>
                                </Box>
                            </Box>
                        </DialogContent>
                    </>
                )}
            </Dialog>
        </>
    );
}

export default function InquiryTraining() {
    const { accesstoken } = useOutletContext();
    const [filters, setFilters] = useState({ organizationId: '', kind: '', changed: '', model: '', q: '' });
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(0);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(null);
    const [tab, setTab] = useState('set');

    useEffect(() => {
        const t = setTimeout(() => setFilters(f => ({ ...f, q: query })), 400);
        return () => clearTimeout(t);
    }, [query]);
    useEffect(() => { setPage(0); }, [filters]);
    useEffect(() => {
        // The access token is already URL-encoded when Monada issues it: add it as it is, not
        // through URLSearchParams, which would encode it a second time.
        const params = new URLSearchParams({ page, pageSize: PAGE_SIZE, ...filters });
        setError(null);
        axios.get(`/api/admin/inquiry-training?accesstoken=${accesstoken}&${params}`)
            .then(({ data }) => setData(data))
            .catch(e => setError(e?.response?.data?.detail || e.message));
    }, [accesstoken, filters, page]);

    const set = key => e => setFilters(f => ({ ...f, [key]: e.target.value }));

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant='h5' sx={{ fontWeight: 700 }}>Inquiry Training</Typography>
            <Typography sx={{ fontSize: 13, color: grey[600], mb: 2, maxWidth: 900 }}>
                Every opportunity the AI wizard filled in, from the mailbox or pasted text: the email it read, what it filled in, and what the user saved.
                Differences are corrections, unless the opportunity was later reused for another shipment.
                {data && ` ${data.total.toLocaleString()} opportunities in ${data.organizations.length} workspaces: the snapshot of all earlier quotes (built ${dayjs(data.built).format('D MMM YYYY')}) and ${(data.liveCount || 0).toLocaleString()} saved since, added live as quotes are saved.`}
            </Typography>

            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab value='set' label='Training set' sx={{ textTransform: 'none' }} />
                <Tab value='corrections' label='Corrections from users' sx={{ textTransform: 'none' }} />
            </Tabs>
            {tab === 'corrections' && <Corrections accesstoken={accesstoken} />}
            {tab === 'set' && <>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                <TextField select size='small' label='Workspace' value={filters.organizationId} onChange={set('organizationId')} sx={{ minWidth: 220 }}>
                    <MenuItem value=''>All workspaces</MenuItem>
                    {(data?.organizations || []).map(o => <MenuItem key={o.id} value={o.id}>{o.title} ({o.count})</MenuItem>)}
                </TextField>
                <TextField select size='small' label='Difference' value={filters.kind} onChange={set('kind')} sx={{ minWidth: 260 }}>
                    <MenuItem value=''>Any</MenuItem>
                    {['mode', 'routes', 'origin', 'destination', 'cargo'].map(k => <MenuItem key={k} value={k}>Any {k} difference</MenuItem>)}
                    {(data?.differences || []).map(d => <MenuItem key={d.name} value={d.name}>{d.name} ({d.count})</MenuItem>)}
                </TextField>
                {/* Each model with how many inquiries it read and the share the user changed, to compare them. */}
                <TextField select size='small' label='Model' value={filters.model} onChange={set('model')} sx={{ minWidth: 300 }}>
                    <MenuItem value=''>All models</MenuItem>
                    {(data?.models || []).map(m => (
                        <MenuItem key={m.name} value={m.name}>
                            {m.name} · {m.count.toLocaleString()} read · {m.count ? Math.round(100 * m.changed / m.count) : 0}% changed
                        </MenuItem>
                    ))}
                </TextField>
                <TextField select size='small' label='Changed by the user' value={filters.changed} onChange={set('changed')} sx={{ minWidth: 180 }}>
                    <MenuItem value=''>All</MenuItem>
                    <MenuItem value='yes'>Changed</MenuItem>
                    <MenuItem value='no'>Saved as filled in</MenuItem>
                </TextField>
                <TextField size='small' label='Search the email text' value={query} onChange={e => setQuery(e.target.value)} sx={{ minWidth: 260 }} />
            </Box>

            {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
            {!data && !error && <CircularProgress size={24} />}
            {data && (
                <Paper variant='outlined'>
                    <TableContainer>
                        <Table size='small'>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Workspace</TableCell>
                                    <TableCell>From</TableCell>
                                    <TableCell>Model</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Differences</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.records.map(r => (
                                    <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpen(r.id)}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(r.created).format('D MMM YYYY')}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.organization}</TableCell>
                                        <TableCell>{r.from === 'mailbox' ? 'Mailbox' : 'Pasted'}</TableCell>
                                        <ModelCell r={r} />
                                        <TableCell sx={{ fontSize: 12, color: grey[700], maxWidth: 480 }}>{r.preview}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {r.differences.length === 0 && <Chip size='small' label='saved as filled in' sx={{ bgcolor: green[50], color: green[800] }} />}
                                                {[...new Set(r.differences)].map(d => <Chip key={d} size='small' label={d} sx={{ bgcolor: blue[50], color: blue[900] }} />)}
                                                {r.maybeReused && <Chip size='small' label='maybe reused' sx={{ bgcolor: orange[50], color: orange[900] }} />}
                                                {r.live && <Chip size='small' label='live' sx={{ bgcolor: green[50], color: green[800] }} />}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination component='div' count={data.matching} page={page} rowsPerPage={PAGE_SIZE} rowsPerPageOptions={[PAGE_SIZE]} onPageChange={(e, p) => setPage(p)} />
                </Paper>
            )}
            <RecordDialog id={open} accesstoken={accesstoken} onClose={() => setOpen(null)} />
            </>}
        </Box>
    );
}
